import React, { useEffect, useState } from "react";
import PageGuide from "../../components/PageGuide";
import { Helmet } from "react-helmet-async";
import ApiCall from "../../utils/apicall";
import { useSelector } from "react-redux";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";
import {
  Container, Card, Stack, Typography, Button, TextField, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Dialog,
  DialogTitle, DialogContent, DialogActions, IconButton, InputAdornment, Box, Tooltip,
  Grid, MenuItem,
} from "@mui/material";
import Iconify from "../../components/iconify";
import { StatusPill, KpiCard } from "../../components/WeldAssure";
import { printPQR } from "../../utils/weldAssurePrint";
import { BrandedDialogHeader, SectionHeader, FIELD_SX } from "../../components/WeldAssure/BrandedForm";

import SmartTable from "../../components/SmartTable/SmartTable";
const EMPTY_FORM = {
  pqrNumber: "", revision: "", pqrDate: "", wpsNumber: "", process: "",
  preparedBy: "", authorizedBy: "", position: "",
  jointType: "", jointBacking: "", rootOpening: "", grooveAngle: "",
  baseMetalSpec: "", baseMetalThickness: "",
  awsSpec: "", awsClass: "", sfaSpec: "",
  currentPolarity: "", amps: "", volts: "", travelIPM: "", heatInput: "",
  preheat: "", interpassMin: "", interpassMax: "", pwht: "",
  tensileSpecimen: "", ultLoad: "", ultStrength: "", failureLoc: "",
  bendType: "", bendResult: "",
  visual: "", rtResult: "", remarks: "",
  testResultsSummary: "", notes: "",
};

function buildDocumentData(form) {
  return {
    position: form.position,
    positionOfGroove: form.position,
    joint: { type: form.jointType, backing: form.jointBacking, rootOpening: form.rootOpening, grooveAngle: form.grooveAngle },
    baseMetalSpec: form.baseMetalSpec, baseMetalThickness: form.baseMetalThickness,
    filler: { awsSpec: form.awsSpec, awsClass: form.awsClass, sfaSpec: form.sfaSpec },
    fillerMetal: form.awsClass,
    electrical: { currentPolarity: form.currentPolarity, amps: form.amps, volts: form.volts, travelIPM: form.travelIPM, heatInput: form.heatInput },
    thermal: { preheat: form.preheat, interpassMin: form.interpassMin, interpassMax: form.interpassMax, pwht: form.pwht },
    tensile: { specimen: form.tensileSpecimen, ultLoad: form.ultLoad, ultStrength: form.ultStrength, failureLoc: form.failureLoc },
    bend: { type: form.bendType, result: form.bendResult },
    exam: { visual: form.visual, rtResult: form.rtResult, remarks: form.remarks },
    testResultsSummary: form.testResultsSummary, notes: form.notes,
  };
}

function flattenDocumentData(doc) {
  const dd = doc.documentData || {};
  const j = dd.joint || {}, fl = dd.filler || {}, el = dd.electrical || {};
  const th = dd.thermal || {}, te = dd.tensile || {}, be = dd.bend || {}, ex = dd.exam || {};
  const g = (x) => x || "";
  return {
    pqrNumber: g(doc.pqrNumber), revision: g(doc.revision), pqrDate: g(doc.pqrDate),
    wpsNumber: g(doc.wpsNumber), process: g(doc.process), preparedBy: g(doc.preparedBy),
    authorizedBy: g(doc.authorizedBy), position: g(dd.position || dd.positionOfGroove),
    jointType: g(j.type), jointBacking: g(j.backing), rootOpening: g(j.rootOpening), grooveAngle: g(j.grooveAngle),
    baseMetalSpec: g(dd.baseMetalSpec), baseMetalThickness: g(dd.baseMetalThickness),
    awsSpec: g(fl.awsSpec), awsClass: g(fl.awsClass || dd.fillerMetal), sfaSpec: g(fl.sfaSpec),
    currentPolarity: g(el.currentPolarity), amps: g(el.amps), volts: g(el.volts), travelIPM: g(el.travelIPM), heatInput: g(el.heatInput),
    preheat: g(th.preheat), interpassMin: g(th.interpassMin), interpassMax: g(th.interpassMax), pwht: g(th.pwht),
    tensileSpecimen: g(te.specimen), ultLoad: g(te.ultLoad), ultStrength: g(te.ultStrength), failureLoc: g(te.failureLoc),
    bendType: g(be.type), bendResult: g(be.result),
    visual: g(ex.visual), rtResult: g(ex.rtResult), remarks: g(ex.remarks),
    testResultsSummary: g(dd.testResultsSummary), notes: g(dd.notes),
  };
}

const YN = ["", "Yes", "No"];
const PASSFAIL = ["", "Pass", "Fail", "N/A"];
const ACCEPT = ["", "Acceptable", "Rejected", "N/A"];

export default function PQRDocuments() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // S-4 approval: Admin and Quality Manager only; the server enforces the same rule.
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveError, setApproveError] = useState("");
  const [approving, setApproving] = useState(false);
  const { loginUser } = useSelector((state) => state.userSlice);
  const userPermissions = loginUser?.decodedToken?.permissions ?? [];
  const canApprove = hasPermission(userPermissions, PERMISSIONS.APPROVE_WELD_PROCEDURE);

  const fetchDocs = async () => {
    try { setLoading(true); const res = await ApiCall.get("/pqr", { params: { search } }); setDocs(res.data?.data || res.data || []); }
    catch (err) { console.error("fetchPQR:", err); } finally { setLoading(false); }
  };
  useEffect(() => { fetchDocs(); /* eslint-disable-next-line */ }, [search]);

  const handleOpen = async (doc = null) => {
    if (doc) { const res = await ApiCall.get(`/pqr/${doc.id}`); const full = res.data?.data || res.data; setEditing(full); setFormData(flattenDocumentData(full)); }
    else { setEditing(null); setFormData(EMPTY_FORM); }
    setOpenForm(true);
  };
  const handleClose = () => { setOpenForm(false); setEditing(null); setFormData(EMPTY_FORM); };
  const f = (field) => (e) => setFormData((p) => ({ ...p, [field]: e.target.value }));

  const handleSave = async () => {
    if (!formData.pqrNumber.trim()) return;
    setSubmitLoading(true);
    try {
      const payload = {
        pqrNumber: formData.pqrNumber, revision: formData.revision, pqrDate: formData.pqrDate || null,
        wpsNumber: formData.wpsNumber, process: formData.process, preparedBy: formData.preparedBy,
        authorizedBy: formData.authorizedBy, documentData: buildDocumentData(formData),
      };
      if (editing) await ApiCall.put(`/pqr/${editing.id}`, payload);
      else await ApiCall.post("/pqr", payload);
      handleClose(); fetchDocs();
    } catch (err) { console.error("savePQR:", err); } finally { setSubmitLoading(false); }
  };
  const handlePrint = async (d) => {
    try { const res = await ApiCall.get(`/pqr/${d.id}`); printPQR(res.data?.data || res.data); }
    catch (err) { console.error("printPQR:", err); printPQR(d); }
  };
  const handleApprove = async () => {
    if (!approveTarget) return;
    setApproving(true); setApproveError("");
    try { await ApiCall.post(`/pqr/${approveTarget.id}/approve`, {}); setApproveTarget(null); fetchDocs(); }
    catch (err) { setApproveError(err?.response?.data?.message || "Approval failed. Try again."); }
    finally { setApproving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try { await ApiCall.delete(`/pqr/${deleteTarget.id}`); setDeleteTarget(null); fetchDocs(); }
    catch (err) { console.error("deletePQR:", err); }
  };

  const Fld = ({ label, field, sm = 3, select, options = [], ...rest }) => (
    <Grid item xs={12} sm={sm}>
      <TextField label={label} value={formData[field] ?? ""} onChange={f(field)} fullWidth size="small" select={select || undefined} sx={FIELD_SX} {...rest}>
        {select && options.map((o) => <MenuItem key={o} value={o}>{o || "—"}</MenuItem>)}
      </TextField>
    </Grid>
  );
  const Sec = ({ children }) => (<Grid item xs={12}><SectionHeader>{children}</SectionHeader></Grid>);

  return (
    <>
      <Helmet><title>PQR Documents | WeldAssure</title></Helmet>
      <Container maxWidth="lg">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4">PQR Documents</Typography>
          <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => handleOpen()}>New PQR</Button>
        </Stack>
        <PageGuide
          why="Keep the test records that prove your welding procedures actually work."
          who="Welding engineers and the QM, keeping procedure-qualification records."
          what="Your Procedure Qualification Records (PQR), each printable as a controlled document."
          how="Record the qualification test results, then Print PDF to produce the controlled PQR."
          when="When a procedure is qualified by test — supporting the matching WPS."
          standard="AWS D1.1 Clause 4 · ASME BPVC Section IX"
        />

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}><KpiCard label="Active PQRs" value={docs.length} sub="qualified records" subKind="success" /></Grid>
          <Grid item xs={12} sm={6} md={3}><KpiCard label="Linked to WPS" value={docs.filter(d => d.wpsNumber).length} sub="supports a WPS" subKind="info" /></Grid>
          <Grid item xs={12} sm={6} md={3}><KpiCard label="Tested" value={docs.filter(d => d.documentData?.testResultsSummary || d.documentData?.tensile?.ultStrength).length} sub="have results" subKind="warning" /></Grid>
          <Grid item xs={12} sm={6} md={3}><KpiCard label="AWS Code" value="D1.1:2020" sub="all conformed" subKind="neutral" /></Grid>
        </Grid>

        <Card sx={{ borderColor: "#E8ECF0", border: "1px solid" }}>
          <Box sx={{ p: 2 }}>
            <TextField size="small" placeholder="Search PQR number, process, WPS…" value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><Iconify icon="eva:search-fill" /></InputAdornment>) }} sx={{ width: 340 }} />
          </Box>
          <TableContainer component={Paper} elevation={0}>
            <SmartTable tableId="pqr-documents" lockedColumns={["PQR Number", "Rev", "Status"]}>
              <TableHead sx={{ "& .MuiTableCell-head": { backgroundColor: "#1C2636", color: "#FFFFFF", fontWeight: 700, fontSize: 11, letterSpacing: 0.3, textTransform: "uppercase", py: 1.5 } }}>
                <TableRow>
                  <TableCell>PQR Number</TableCell><TableCell>Rev</TableCell><TableCell>Date</TableCell>
                  <TableCell>Process</TableCell><TableCell>Supporting WPS</TableCell><TableCell>Prepared By</TableCell>
                  <TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ "& .MuiTableRow-root:nth-of-type(even)": { backgroundColor: "#F7F9FC" }, "& .MuiTableCell-body": { borderBottomColor: "#E8ECF0", py: 1.5 } }}>
                {loading ? (<TableRow><TableCell colSpan={8} align="center">Loading…</TableCell></TableRow>)
                : docs.length === 0 ? (<TableRow><TableCell colSpan={8} align="center">No PQR documents found</TableCell></TableRow>)
                : docs.map((d) => {
                    const isSuperseded = d.approvalStatus === "superseded";
                    const isDraft = d.approvalStatus === "pending" || !d.approvalStatus;
                    const statusLabel = isSuperseded ? "Superseded" : isDraft ? "Draft" : "Approved";
                    const statusKind = isSuperseded ? "neutral" : isDraft ? "warning" : "success";
                    return (
                      <TableRow key={d.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700} sx={{ color: "#141A24" }}>{d.pqrNumber}</Typography>
                          <Typography variant="caption" sx={{ color: "#959EA8", fontSize: 10 }}>AWS D1.1 Clause 4</Typography>
                        </TableCell>
                        <TableCell><Typography variant="body2" fontWeight={600}>{d.revision || "—"}</Typography></TableCell>
                        <TableCell><Typography variant="body2" sx={{ color: "#555F6B" }}>{d.pqrDate || "—"}</Typography></TableCell>
                        <TableCell><Typography variant="body2" fontWeight={500}>{d.process || "—"}</Typography></TableCell>
                        <TableCell>{d.wpsNumber || "—"}</TableCell>
                        <TableCell>{d.preparedBy || "—"}</TableCell>
                        <TableCell><Tooltip title={d.approvedAt ? `Approved by ${d.approvedByName || "user " + d.approvedBy} on ${String(d.approvedAt).slice(0, 10)}` : ""}><span><StatusPill label={statusLabel} kind={statusKind} /></span></Tooltip></TableCell>
                        <TableCell align="right">
                          <Tooltip title="Print PDF"><IconButton size="small" onClick={() => handlePrint(d)}><Iconify icon="eva:printer-fill" /></IconButton></Tooltip>
                          {isDraft && canApprove && (
                            <Tooltip title="Approve"><IconButton size="small" color="success" onClick={() => { setApproveError(""); setApproveTarget(d); }}><Iconify icon="eva:checkmark-circle-2-fill" /></IconButton></Tooltip>
                          )}
                          {isDraft ? (
                          <>
                          <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpen(d)}><Iconify icon="eva:edit-fill" /></IconButton></Tooltip>
                          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteTarget(d)}><Iconify icon="eva:trash-2-fill" /></IconButton></Tooltip>
                          </>
                          ) : (
                            <Tooltip title="Approved records are locked. Create a new revision to change it."><IconButton size="small" disabled><Iconify icon="eva:lock-fill" /></IconButton></Tooltip>
                          )}
                        </TableCell>
                      </TableRow>);
                  })}
              </TableBody>
            </SmartTable>
          </TableContainer>
        </Card>
      </Container>

      <Dialog open={openForm} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <BrandedDialogHeader title={editing ? `Edit PQR — ${editing.pqrNumber}` : "New PQR Document"} subtitle="Procedure Qualification Record · AWS D1.1 Clause 4" code="AWS D1.1:2020" onClose={handleClose} />
        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={1.5}>
            <Sec>Header</Sec>
            <Fld label="PQR Number *" field="pqrNumber" sm={4} />
            <Fld label="Revision" field="revision" sm={2} />
            <Fld label="Date" field="pqrDate" sm={3} type="date" InputLabelProps={{ shrink: true }} />
            <Fld label="Supporting WPS" field="wpsNumber" sm={3} />
            <Fld label="Prepared By" field="preparedBy" sm={4} />
            <Fld label="Authorized By" field="authorizedBy" sm={4} />
            <Fld label="Process(es)" field="process" sm={2} />
            <Fld label="Position" field="position" sm={2} placeholder="e.g. 1G" />

            <Sec>Joint Design</Sec>
            <Fld label="Joint Type" field="jointType" />
            <Fld label="Backing" field="jointBacking" select options={YN} />
            <Fld label="Root Opening" field="rootOpening" />
            <Fld label="Groove Angle" field="grooveAngle" />
            <Fld label="Base Metal Spec" field="baseMetalSpec" placeholder="e.g. A572 Gr50" />
            <Fld label="Base Metal Thickness" field="baseMetalThickness" />

            <Sec>Filler Metals</Sec>
            <Fld label="AWS Spec." field="awsSpec" />
            <Fld label="AWS Class." field="awsClass" />
            <Fld label="SFA Spec." field="sfaSpec" />

            <Sec>Electrical (As-Tested)</Sec>
            <Fld label="Current / Polarity" field="currentPolarity" />
            <Fld label="Amps / WFS" field="amps" />
            <Fld label="Volts" field="volts" />
            <Fld label="Travel IPM" field="travelIPM" />
            <Fld label="Heat Input" field="heatInput" sm={3} />

            <Sec>Thermal Controls</Sec>
            <Fld label="Preheat (°F)" field="preheat" />
            <Fld label="Interpass Min." field="interpassMin" />
            <Fld label="Interpass Max." field="interpassMax" />
            <Fld label="PWHT" field="pwht" />

            <Sec>Tensile Test</Sec>
            <Fld label="Specimen" field="tensileSpecimen" />
            <Fld label="Ult. Load" field="ultLoad" />
            <Fld label="Ult. Strength" field="ultStrength" />
            <Fld label="Failure Location" field="failureLoc" />

            <Sec>Guided Bend</Sec>
            <Fld label="Type" field="bendType" placeholder="e.g. Face / Root / Side" />
            <Fld label="Result" field="bendResult" select options={PASSFAIL} />

            <Sec>Examination</Sec>
            <Fld label="Visual" field="visual" select options={ACCEPT} />
            <Fld label="RT Result" field="rtResult" />
            <Fld label="Remarks" field="remarks" sm={6} />

            <Sec>Results Summary</Sec>
            <Grid item xs={12}><TextField label="Test Results Summary" value={formData.testResultsSummary} onChange={f("testResultsSummary")} fullWidth multiline rows={2} size="small" sx={FIELD_SX} /></Grid>
            <Grid item xs={12}><TextField label="Notes" value={formData.notes} onChange={f("notes")} fullWidth multiline rows={2} size="small" sx={FIELD_SX} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ bgcolor: "#F7F9FC", borderTop: "1px solid #E8ECF0", px: 3, py: 1.75 }}>
          <Button onClick={handleClose} sx={{ color: "text.secondary" }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={submitLoading || !formData.pqrNumber.trim()}>
            {submitLoading ? "Saving…" : editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!approveTarget} onClose={() => !approving && setApproveTarget(null)} maxWidth="xs">
        <DialogTitle>Approve PQR {approveTarget?.pqrNumber}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Your name and today's date are recorded on this PQR and in the audit log. Once approved, the record is locked; a change needs a new revision. The person who created the record cannot approve it.</Typography>
          {approveError && <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>{approveError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveTarget(null)} disabled={approving}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleApprove} disabled={approving}>{approving ? "Approving…" : "Approve"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogContent sx={{ pt: 3 }}><Typography>Permanently remove PQR <strong>{deleteTarget?.pqrNumber}</strong>?</Typography></DialogContent>
        <DialogActions><Button onClick={() => setDeleteTarget(null)}>Cancel</Button><Button variant="contained" color="error" onClick={handleDelete}>Delete</Button></DialogActions>
      </Dialog>
    </>
  );
}
